import { Injectable } from '@angular/core';
import { FilterService } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class MoPrimengCustomFilterService extends FilterService {
  constructor() {
    super();
    this.registerCustomFilters();
  }

  /**
   * Registers all custom filter functions
   */
  private registerCustomFilters(): void {
    // Simple field contains for direct string values
    this.register('field-contains', (fieldValue: any, filter: string): boolean => {
      if (filter === undefined || filter === null || filter.trim() === '') {
        return true;
      }

      if (fieldValue === undefined || fieldValue === null) {
        return false;
      }

      // Convert to string and search - simple contains
      const stringValue = fieldValue.toString().toLowerCase();
      const result = stringValue.includes(filter.toLowerCase());
      return result;
    });

    // Universal nested contains filter - works with complex field values from PrimeNG
    this.register('nested-contains', (fieldValue: any, filter: string): boolean => {
      if (filter === undefined || filter === null || filter.trim() === '') {
        return true;
      }

      if (fieldValue === undefined || fieldValue === null) {
        return false;
      }

      return this.searchInValue(fieldValue, filter.toLowerCase());
    });

    // Specific nested equals filter for exact matches
    this.register('nested-equals', (fieldValue: any, filter: string): boolean => {
      if (filter === undefined || filter === null || filter.trim() === '') {
        return true;
      }

      if (fieldValue === undefined || fieldValue === null) {
        return false;
      }

      return this.searchForExactMatch(fieldValue, filter.toLowerCase());
    });
  }

  /**
   * Legacy filter method for backwards compatibility
   */
  override filter(value: any[], fields: any[], filterValue: string): any[] {
    if (!value || !fields || !filterValue) {
      return value;
    }

    return value.filter((item) => {
      return fields.some((field) => {
        const fieldValue = this.getNestedProperty(item, field);
        return this.searchInValue(fieldValue, filterValue.toLowerCase());
      });
    });
  }

  /**
   * Recursively searches all properties of an object/array for a contains match
   */
  private searchInValue(value: any, searchTerm: string): boolean {
    // Null/undefined check
    if (value === null || value === undefined) {
      return false;
    }

    // String check - case insensitive contains
    if (typeof value === 'string') {
      return value.toLowerCase().includes(searchTerm);
    }

    // Convert Number/Boolean to string and check
    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString().toLowerCase().includes(searchTerm);
    }

    // Convert Date to string
    if (value instanceof Date) {
      return value.toISOString().toLowerCase().includes(searchTerm);
    }

    // Search through array
    if (Array.isArray(value)) {
      return value.some((item) => this.searchInValue(item, searchTerm));
    }

    // Recursively search object properties
    if (typeof value === 'object') {
      return Object.values(value).some((prop) => this.searchInValue(prop, searchTerm));
    }

    return false;
  }

  /**
   * Searches for exact matches in nested structures
   */
  private searchForExactMatch(value: any, searchTerm: string): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.toLowerCase() === searchTerm;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString().toLowerCase() === searchTerm;
    }

    if (value instanceof Date) {
      return value.toISOString().toLowerCase() === searchTerm;
    }

    if (Array.isArray(value)) {
      return value.some((item) => this.searchForExactMatch(item, searchTerm));
    }

    if (typeof value === 'object') {
      return Object.values(value).some((prop) => this.searchForExactMatch(prop, searchTerm));
    }

    return false;
  }

  /**
   * Searches for startsWith matches in nested structures
   */
  private searchWithStartsWith(value: any, searchTerm: string): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.toLowerCase().startsWith(searchTerm);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString().toLowerCase().startsWith(searchTerm);
    }

    if (value instanceof Date) {
      return value.toISOString().toLowerCase().startsWith(searchTerm);
    }

    if (Array.isArray(value)) {
      return value.some((item) => this.searchWithStartsWith(item, searchTerm));
    }

    if (typeof value === 'object') {
      return Object.values(value).some((prop) => this.searchWithStartsWith(prop, searchTerm));
    }

    return false;
  }

  /**
   * Searches for endsWith matches in nested structures
   */
  private searchWithEndsWith(value: any, searchTerm: string): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.toLowerCase().endsWith(searchTerm);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString().toLowerCase().endsWith(searchTerm);
    }

    if (value instanceof Date) {
      return value.toISOString().toLowerCase().endsWith(searchTerm);
    }

    if (Array.isArray(value)) {
      return value.some((item) => this.searchWithEndsWith(item, searchTerm));
    }

    if (typeof value === 'object') {
      return Object.values(value).some((prop) => this.searchWithEndsWith(prop, searchTerm));
    }

    return false;
  }

  /**
   * Helper method to extract nested properties
   */
  public getNestedProperty(obj: any, path: string): any {
    if (!obj || !path) {
      return null;
    }

    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  }

  /**
   * Utility method for complex custom filtering
   */
  public filterByMultipleFields(
    data: any[],
    searchTerm: string,
    fields: string[],
    matchMode: 'contains' | 'equals' | 'startsWith' | 'endsWith' = 'contains'
  ): any[] {
    if (!data || !searchTerm || !fields || fields.length === 0) {
      return data;
    }

    const searchMethod = this.getSearchMethod(matchMode);

    return data.filter((item) => {
      return fields.some((field) => {
        const value = this.getNestedProperty(item, field);
        return searchMethod(value, searchTerm.toLowerCase());
      });
    });
  }

  /**
   * Returns the corresponding search method
   */
  private getSearchMethod(matchMode: string): (value: any, searchTerm: string) => boolean {
    switch (matchMode) {
      case 'equals':
        return this.searchForExactMatch.bind(this);
      case 'startsWith':
        return this.searchWithStartsWith.bind(this);
      case 'endsWith':
        return this.searchWithEndsWith.bind(this);
      case 'contains':
      default:
        return this.searchInValue.bind(this);
    }
  }
}
